import { useEffect, useMemo } from "react";
import actions from "store/actions";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { subscriptionPricesSelector, userMetadataSelector } from "store/selectors";

import { getComprehensibleSubscriptionPrice } from "helpers/subscription/price";

import { querySubscriptionControllerGetActualPriceInfo } from "utils/swagger_react_query";

/**
@param setIsLoading - function to set isLoading state
  - Hook is used to fetch subscription prices from the API and store them in the Redux store.
  - If there are no prices in the Redux store, the prices will be fetched from the API.
*/
const useSubscriptionPrices = ({
  setIsLoading,
}: {
  setIsLoading?: (isLoading: boolean) => void;
}) => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(userMetadataSelector);
  const subscriptionPrices = useAppSelector(subscriptionPricesSelector);
  const comprehensibleSubscriptionPrices = useMemo(() => {
    if (!subscriptionPrices) return { basePrice: "", additionalPrice: "" };
    return getComprehensibleSubscriptionPrice(subscriptionPrices);
  }, [subscriptionPrices]);

  useEffect(() => {
    if (!subscriptionPrices && currentUser?.activeCompanyId) {
      fetchPrices();
    }
  }, [subscriptionPrices, currentUser?.activeCompanyId]);

  const fetchPrices = async () => {
    if (subscriptionPrices) return;

    setIsLoading?.(true);

    try {
      const prices = await querySubscriptionControllerGetActualPriceInfo();
      dispatch(actions.subscriptionPricesData.setSubscriptionPrices(prices || null));
    } catch (err) {
      console.error("Error fetching subscription prices", err);
    } finally {
      setIsLoading?.(false);
    }
  };

  return {
    refetch: fetchPrices,
    prices: subscriptionPrices,
    comprehensiblePrices: comprehensibleSubscriptionPrices,
  };
};

export default useSubscriptionPrices;
